// src/services/enrollmentsService.js
import api from './api';

const enrollmentsService = {
  // Get all enrollments with filtering and pagination
  getEnrollments: async (params = {}) => {
    try {
      console.log('EnrollmentsService - Fetching enrollments with params:', params);
      
      const response = await api.get('/enrollments', params);
      console.log('EnrollmentsService - Response:', response);
      
      return {
        enrollments: response.enrollments || [],
        pagination: response.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 50,
          hasNextPage: false,
          hasPrevPage: false
        }
      };
    } catch (error) {
      console.error('EnrollmentsService - Error fetching enrollments:', error);
      throw new Error(error.message || 'Failed to fetch enrollments');
    }
  },

  // Get single enrollment
  getEnrollment: async (id) => {
    try {
      console.log('EnrollmentsService - Fetching enrollment:', id);
      
      const response = await api.get(`/enrollments/${id}`);
      console.log('EnrollmentsService - Enrollment response:', response);
      
      return response;
    } catch (error) {
      console.error('EnrollmentsService - Error fetching enrollment:', error);
      throw new Error(error.message || 'Failed to fetch enrollment');
    }
  },

  // Create new enrollment
  createEnrollment: async (enrollmentData) => {
    try {
      console.log('EnrollmentsService - Creating enrollment:', enrollmentData);
      
      const response = await api.post('/enrollments', enrollmentData);
      console.log('EnrollmentsService - Created enrollment:', response);
      
      return response;
    } catch (error) {
      console.error('EnrollmentsService - Error creating enrollment:', error);
      throw new Error(error.message || 'Failed to create enrollment');
    }
  },

  // Update enrollment
  updateEnrollment: async (id, updates) => {
    try {
      console.log('EnrollmentsService - Updating enrollment:', id, updates);
      
      const response = await api.put(`/enrollments/${id}`, updates);
      console.log('EnrollmentsService - Updated enrollment:', response);
      
      return response;
    } catch (error) {
      console.error('EnrollmentsService - Error updating enrollment:', error);
      throw new Error(error.message || 'Failed to update enrollment');
    }
  },

  // Cancel single enrollment
  cancelEnrollment: async (id) => {
    try {
      console.log('EnrollmentsService - Cancelling enrollment:', id);
      
      const response = await api.delete(`/enrollments/${id}`);
      console.log('EnrollmentsService - Cancelled enrollment:', response);
      
      return response;
    } catch (error) {
      console.error('EnrollmentsService - Error cancelling enrollment:', error);
      throw new Error(error.message || 'Failed to cancel enrollment');
    }
  },

  // Cancel multiple enrollments
  cancelEnrollments: async (ids) => {
    try {
      console.log('EnrollmentsService - Bulk cancelling enrollments:', ids);

      const response = await api.delete('/enrollments/bulk', { ids });
      console.log('EnrollmentsService - Bulk cancelled enrollments:', response);

      return response;
    } catch (error) {
      console.error('EnrollmentsService - Error bulk cancelling enrollments:', error);
      throw new Error(error.message || 'Failed to cancel enrollments');
    }
  },

  // Get available classes for a student
  getAvailableClasses: async (studentId) => {
    try {
      console.log('EnrollmentsService - Fetching available classes for student:', studentId);
      
      const response = await api.get(`/enrollments/available-classes/${studentId}`);
      console.log('EnrollmentsService - Available classes:', response);
      
      return response;
    } catch (error) {
      console.error('EnrollmentsService - Error fetching available classes:', error);
      throw new Error(error.message || 'Failed to fetch available classes');
    }
  },

  // Get enrollment statistics
  getEnrollmentStats: async () => {
    try {
      console.log('EnrollmentsService - Fetching enrollment stats');
      
      const response = await api.get('/enrollments/stats/overview');
      console.log('EnrollmentsService - Enrollment stats:', response);
      
      return response;
    } catch (error) {
      console.error('EnrollmentsService - Error fetching enrollment stats:', error);
      throw new Error(error.message || 'Failed to fetch enrollment statistics');
    }
  },

  // Transform enrollment data from backend format to frontend format
  transformEnrollmentFromBackend: (enrollment) => {
    return {
      // Core enrollment fields
      id: enrollment.id,
      studentId: enrollment.student_id,
      classId: enrollment.class_id,
      enrollmentDate: enrollment.enrollment_date,
      paymentStatus: enrollment.payment_status,
      enrollmentStatus: enrollment.enrollment_status,
      emergencyContact: enrollment.emergency_contact,
      medicalNotes: enrollment.medical_notes,
      notes: enrollment.notes,
      parentConsent: enrollment.parent_consent,
      amountPaid: enrollment.amount_paid,
      totalAmount: enrollment.total_amount,
      createdAt: enrollment.created_at,
      updatedAt: enrollment.updated_at,

      // Student information
      studentName: `${enrollment.student_first_name || ''} ${enrollment.student_last_name || ''}`.trim(),
      studentFirstName: enrollment.student_first_name,
      studentLastName: enrollment.student_last_name,
      studentAge: enrollment.student_age,
      studentGender: enrollment.gender,
      studentPhoto: enrollment.student_photo,
      parentName: enrollment.parent_name,
      parentEmail: enrollment.parent_email,
      parentPhone: enrollment.parent_phone,

      // Class information
      className: enrollment.class_name,
      instructor: enrollment.instructor,
      classStartDate: enrollment.class_start_date,
      classEndDate: enrollment.class_end_date,
      startTime: enrollment.start_time,
      duration: enrollment.duration,
      location: enrollment.location,
      classSchedule: enrollment.class_schedule,
      classPrice: enrollment.class_price || enrollment.total_amount,

      // For compatibility with existing frontend code
      student_first_name: enrollment.student_first_name,
      student_last_name: enrollment.student_last_name,
      student_age: enrollment.student_age,
      gender: enrollment.gender,
      student_photo: enrollment.student_photo,
      parent_name: enrollment.parent_name,
      parent_email: enrollment.parent_email,
      parent_phone: enrollment.parent_phone,
      class_name: enrollment.class_name,
      class_schedule: enrollment.class_schedule
    };
  },

  // Transform multiple enrollments
  transformEnrollmentsFromBackend: (enrollments) => {
    return enrollments.map(enrollment => enrollmentsService.transformEnrollmentFromBackend(enrollment));
  },

  // Get enrollment history
  getEnrollmentHistory: async (id) => {
    try {
      console.log('EnrollmentsService - Fetching enrollment history for:', id);
      
      const response = await api.get(`/enrollments/${id}/history`);
      console.log('EnrollmentsService - Enrollment history:', response);
      
      return response;
    } catch (error) {
      console.error('EnrollmentsService - Error fetching enrollment history:', error);
      throw new Error(error.message || 'Failed to fetch enrollment history');
    }
  },

  // Bulk update enrollments
  bulkUpdateEnrollments: async (enrollmentIds, updates) => {
    try {
      console.log('EnrollmentsService - Bulk updating enrollments:', enrollmentIds, updates);
      
      const response = await api.put('/enrollments/bulk', {
        enrollmentIds,
        updates
      });
      console.log('EnrollmentsService - Bulk update response:', response);
      
      return response;
    } catch (error) {
      console.error('EnrollmentsService - Error bulk updating enrollments:', error);
      throw new Error(error.message || 'Failed to bulk update enrollments');
    }
  }
};

export default enrollmentsService;